import { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { getPageTitle } from "../utils/pageTitles";
import { selectAuth, useAppSelector, useRecordUserActivityEventMutation } from "../store";

/** Resolves numeric emp_id from JWT subject or persisted username. */
function resolveEmpId(auth) {
  const sub = auth?.decodedToken?.sub;
  if (sub != null && String(sub).trim() !== "") {
    const n = Number.parseInt(String(sub).trim(), 10);
    if (Number.isFinite(n)) {
      return n;
    }
  }
  const username = auth?.username;
  if (username != null && String(username).trim() !== "") {
    const n = Number.parseInt(String(username).trim(), 10);
    if (Number.isFinite(n)) {
      return n;
    }
  }
  return null;
}

/**
 * Logs PAGE_VIEW on protected route changes. Mounted once in {@link ProtectedLayout}.
 * Session id is read from the JWT on the server ({@code sessionId} claim).
 */
export function ActivityTracker() {
  const location = useLocation();
  const auth = useAppSelector(selectAuth);
  const [recordEvent] = useRecordUserActivityEventMutation();
  /** Suppress duplicate POSTs for the same route within a short window (e.g. React Strict Mode). */
  const lastLoggedRef = useRef({ routeKey: "", loggedAt: 0 });

  useEffect(() => {
    if (!auth?.accessToken) {
      return undefined;
    }

    const empId = resolveEmpId(auth);
    if (empId == null) {
      return undefined;
    }

    const pagePath =
      location.pathname + (location.search && location.search.length > 0 ? location.search : "");
    const routeKey = pagePath;
    const now = Date.now();
    if (
      lastLoggedRef.current.routeKey === routeKey &&
      now - lastLoggedRef.current.loggedAt < 500
    ) {
      return undefined;
    }
    lastLoggedRef.current = { routeKey, loggedAt: now };

    const pageTitle = getPageTitle(location.pathname, location.search);

    recordEvent({
      empId,
      eventType: "PAGE_VIEW",
      eventCategory: "NAVIGATION",
      pagePath,
      pageTitle,
    }).catch(() => {
      /* fire-and-forget; do not block navigation */
    });

    return undefined;
  }, [
    auth,
    location.pathname,
    location.search,
    recordEvent,
  ]);

  return null;
}
